import { useState, useRef, useEffect } from "react";
import { Input, Button, Alert } from "antd";
import { Bot, Send, User } from "lucide-react";
import { useAiAgentChat } from "../../../hooks/api/ai-agent/hooks";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const NOT_CONFIGURED_KEYWORDS = ["not configured", "not set up", "no api key", "agent not configured", "ai agent not configured"];

const isNotConfiguredError = (text: string) =>
  NOT_CONFIGURED_KEYWORDS.some((kw) => text.toLowerCase().includes(kw));

const MessageBubble = ({ msg }: { msg: ChatMessage }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? "bg-primary text-white" : "bg-slate-100 text-slate-600"
        }`}
      >
        {isUser ? <User size={15} /> : <Bot size={15} />}
      </div>
      <div
        className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? "bg-primary text-white rounded-tr-sm"
            : "bg-slate-100 text-slate-800 rounded-tl-sm"
        }`}
      >
        {msg.content}
      </div>
    </div>
  );
};

const AIAgentPage = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Hello! I'm your AI assistant. I can help you with questions about your field operations, jobs, timesheets, and more. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [notConfigured, setNotConfigured] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const chat = useAiAgentChat({
    onSuccess: (res: any) => {
      const reply = res?.data?.reply || res?.reply || res?.message || "No response received.";
      if (isNotConfiguredError(reply)) {
        setNotConfigured(true);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "AI assistant is not yet configured for your account. Contact support to enable this feature.",
          },
        ]);
      } else {
        setNotConfigured(false);
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      }
    },
    onError: (err: any) => {
      const errMsg =
        err?.response?.data?.message || err?.message || "Something went wrong.";
      if (isNotConfiguredError(errMsg)) {
        setNotConfigured(true);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "AI assistant is not yet configured for your account. Contact support to enable this feature.",
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Sorry, I encountered an error: ${errMsg}`,
          },
        ]);
      }
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || chat.isPending) return;

    const userMsg: ChatMessage = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    // Build history for context (exclude the first greeting message)
    const history: ChatMessage[] = messages.slice(1).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    chat.mutate({ message: trimmed, history });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="p-6 min-h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Bot size={20} className="text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-800">AI Assistant</h1>
          <p className="text-slate-500 text-sm">Ask anything about your operations</p>
        </div>
      </div>

      {notConfigured && (
        <Alert
          type="warning"
          className="mb-4"
          message="AI Assistant Not Configured"
          description="The AI assistant is not yet configured for your account. Please contact support to enable this feature."
          showIcon
        />
      )}

      {/* Chat window */}
      <div className="flex-1 bg-white rounded-xl border border-slate-100 shadow-sm flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5" style={{ minHeight: 400, maxHeight: "calc(100vh - 280px)" }}>
          {messages.map((msg, idx) => (
            <MessageBubble key={idx} msg={msg} />
          ))}

          {/* Typing indicator */}
          {chat.isPending && (
            <div className="flex gap-3 mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                <Bot size={15} />
              </div>
              <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-tl-sm">
                <div className="flex gap-1 items-center h-4">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-slate-100 p-4 flex gap-3 items-end bg-slate-50/50">
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1 resize-none"
            disabled={chat.isPending}
          />
          <Button
            type="primary"
            icon={<Send size={14} />}
            onClick={handleSend}
            loading={chat.isPending}
            disabled={!input.trim()}
            className="!h-auto !py-2 !px-4 flex items-center gap-1.5"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIAgentPage;
